import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles, Gift, Target, Video, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface OnboardingFlowProps {
    onComplete: () => void;
}

// Tutorial Slides Content
const TUTORIAL_SLIDES = [
    {
        icon: Video,
        title: 'Bem-vindo à Publee! 🎬',
        description: 'Crie vídeos UGC profissionais em segundos com inteligência artificial.',
        highlight: 'Sem precisar gravar, editar ou contratar creators.',
    },
    {
        icon: Zap,
        title: 'Como funciona?',
        description: 'Escolha um avatar, escreva seu script e pronto! A IA gera seu vídeo.',
        highlight: 'Simples assim.',
    },
    {
        icon: Target,
        title: 'Complete Missões 🎯',
        description: 'Ganhe créditos grátis completando missões e indicando amigos.',
        highlight: 'Quanto mais você usa, mais ganha.',
    },
];

// ICP Questions
const ICP_QUESTIONS = [
    {
        id: 'how_discovered',
        title: 'Como você conheceu a Publee?',
        subtitle: 'Isso nos ajuda a melhorar nosso alcance',
        options: [
            { value: 'google', label: 'Google' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'youtube', label: 'YouTube' },
            { value: 'friend', label: 'Indicação de amigo' },
            { value: 'ads', label: 'Anúncios' },
            { value: 'other', label: 'Outro' },
        ],
    },
    {
        id: 'occupation',
        title: 'Qual sua ocupação?',
        subtitle: 'Queremos entender melhor nossos usuários',
        options: [
            { value: 'content_creator', label: 'Criador de conteúdo' },
            { value: 'marketer', label: 'Profissional de marketing' },
            { value: 'business_owner', label: 'Dono de negócio' },
            { value: 'freelancer', label: 'Freelancer' },
            { value: 'student', label: 'Estudante' },
            { value: 'other', label: 'Outro' },
        ],
    },
    {
        id: 'main_goal',
        title: 'Qual seu principal objetivo?',
        subtitle: 'Vamos personalizar sua experiência',
        options: [
            { value: 'grow_followers', label: 'Crescer seguidores' },
            { value: 'sell_products', label: 'Vender produtos' },
            { value: 'brand_content', label: 'Criar conteúdo para marcas' },
            { value: 'personal_brand', label: 'Fortalecer marca pessoal' },
            { value: 'other', label: 'Outro' },
        ],
    },
];

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
    const { toast } = useToast();
    const { user } = useAuth();
    const [phase, setPhase] = useState<'tutorial' | 'icp'>('tutorial');
    const [tutorialStep, setTutorialStep] = useState(0);
    const [icpStep, setIcpStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [answers, setAnswers] = useState<Record<string, string>>({
        how_discovered: '',
        occupation: '',
        main_goal: '',
    });

    const totalTutorialSteps = TUTORIAL_SLIDES.length;
    const totalIcpSteps = ICP_QUESTIONS.length;

    // Detect platform
    const detectPlatform = () => {
        const ua = navigator.userAgent;
        if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
        if (/Android/.test(ua)) return 'android';
        return 'desktop';
    };

    // Tutorial Navigation
    const handleTutorialNext = () => {
        if (tutorialStep < totalTutorialSteps - 1) {
            setTutorialStep(prev => prev + 1);
        } else {
            setPhase('icp');
        }
    };

    const handleTutorialPrev = () => {
        if (tutorialStep > 0) {
            setTutorialStep(prev => prev - 1);
        }
    };

    // ICP Navigation
    const handleIcpSelect = (value: string) => {
        const currentQuestion = ICP_QUESTIONS[icpStep];
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    };

    const handleIcpNext = () => {
        if (icpStep < totalIcpSteps - 1) {
            setIcpStep(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const handleSkip = () => {
        handleSubmit();
    };

    const handleSubmit = async () => {
        if (!user) return;

        setIsSubmitting(true);
        try {
            const platform = detectPlatform();

            // Always give 1 credit for completing onboarding (not 3)
            const creditsEarned = 1;

            // Store in localStorage as backup
            localStorage.setItem('publee_onboarding', JSON.stringify({
                ...answers,
                platform,
                completed_at: new Date().toISOString(),
                user_id: user.id,
            }));

            // Update profile with ICP data
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    how_discovered: answers.how_discovered || null,
                    occupation: answers.occupation || null,
                    main_goal: answers.main_goal || null,
                    onboarding_completed_at: new Date().toISOString(),
                } as any)
                .eq('id', user.id);

            if (profileError) {
                console.error('Profile update error:', profileError);
            }

            // Award 1 credit to user for completing onboarding
            try {
                await supabase.rpc('add_credits_with_tracking', {
                    p_user_id: user.id,
                    p_amount: creditsEarned,
                    p_credit_type: 'bonus',
                    p_source_type: 'onboarding',
                    p_source_id: 'onboarding_complete',
                    p_description: 'Bônus: completou o onboarding',
                });

                toast({
                    title: '🎉 +1 crédito!',
                    description: 'Obrigado por completar seu perfil!',
                });
            } catch (rpcError) {
                console.error('Credits RPC error:', rpcError);
            }

            // CASCADE EFFECT: Reward referrer if this user was referred
            try {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('referred_by_code')
                    .eq('id', user.id)
                    .single();

                if (profile?.referred_by_code) {
                    // Find who referred this user and give them 1 credit
                    const { data: referrerProfile } = await supabase
                        .from('referral_codes')
                        .select('user_id')
                        .eq('code', profile.referred_by_code)
                        .single();

                    if (referrerProfile?.user_id) {
                        await supabase.rpc('add_credits_with_tracking', {
                            p_user_id: referrerProfile.user_id,
                            p_amount: 1,
                            p_credit_type: 'referral',
                            p_source_type: 'referral_onboarding',
                            p_source_id: user.id,
                            p_description: 'Bônus: seu indicado completou o onboarding',
                        });
                    }
                }
            } catch (referralError) {
                console.error('Referral reward error:', referralError);
            }

            onComplete();
        } catch (err) {
            console.error('Onboarding error:', err);
            onComplete();
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentQuestion = ICP_QUESTIONS[icpStep];
    const currentSlide = TUTORIAL_SLIDES[tutorialStep];
    const filledFields = Object.values(answers).filter(v => v !== '').length;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/95 backdrop-blur-md z-50 flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
                {/* PHASE: Tutorial */}
                {phase === 'tutorial' && (
                    <>
                        {/* Header */}
                        <div className="p-6 text-center">
                            <div className="inline-flex p-4 bg-primary/20 rounded-2xl mb-4">
                                <currentSlide.icon className="w-10 h-10 text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">{currentSlide.title}</h2>
                            <p className="text-muted-foreground mb-2">{currentSlide.description}</p>
                            <p className="text-sm font-medium text-primary">{currentSlide.highlight}</p>
                        </div>

                        {/* Progress dots */}
                        <div className="flex justify-center gap-2 pb-4">
                            {TUTORIAL_SLIDES.map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full transition-colors ${i === tutorialStep ? 'bg-primary w-6' : 'bg-primary/30'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-border bg-secondary/30 flex justify-between">
                            <Button
                                variant="ghost"
                                onClick={handleTutorialPrev}
                                disabled={tutorialStep === 0}
                                className="gap-2"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Voltar
                            </Button>
                            <Button onClick={handleTutorialNext} className="gap-2">
                                {tutorialStep === totalTutorialSteps - 1 ? 'Começar' : 'Próximo'}
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </>
                )}

                {/* PHASE: ICP Questions */}
                {phase === 'icp' && (
                    <>
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/20 rounded-lg">
                                        <Gift className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold text-lg">Última etapa! 🎁</h2>
                                        <p className="text-sm text-muted-foreground">
                                            Ganhe 1 crédito grátis
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSkip}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    disabled={isSubmitting}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Progress */}
                            <div className="flex gap-1 mt-4">
                                {ICP_QUESTIONS.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`h-1 flex-1 rounded-full transition-colors ${i <= icpStep ? 'bg-primary' : 'bg-primary/20'
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <h3 className="text-lg font-medium mb-1">{currentQuestion.title}</h3>
                            <p className="text-sm text-muted-foreground mb-4">{currentQuestion.subtitle}</p>

                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {currentQuestion.options.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleIcpSelect(option.value)}
                                        className={`w-full p-3 rounded-lg border text-left transition-all ${answers[currentQuestion.id] === option.value
                                            ? 'border-primary bg-primary/10 text-foreground'
                                            : 'border-border hover:border-primary/50 hover:bg-secondary'
                                            }`}
                                    >
                                        <span className="font-medium">{option.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-border bg-secondary/30">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Sparkles className="w-4 h-4 text-primary" />
                                    <span>+1 crédito ao completar</span>
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        onClick={handleSkip}
                                        disabled={isSubmitting}
                                    >
                                        Pular
                                    </Button>
                                    <Button
                                        onClick={handleIcpNext}
                                        disabled={isSubmitting}
                                        className="gap-2"
                                    >
                                        {icpStep === totalIcpSteps - 1 ? 'Finalizar' : 'Próximo'}
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
}
